import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Badge } from '@/components/ui/badge';
import type { SpamReport } from '@/types/email';

type SpamScoreBadgeProps = {
  score?: number | null;
  requiredScore?: number | null;
  action?: string | null;
  report?: SpamReport | null;
  scannedAt?: string | null;
  size?: 'sm' | 'md';
  className?: string;
};

const LEVEL_STYLES: Record<string, string> = {
  good: 'bg-info-surface text-info-ink border-info-line',
  low: 'bg-success-surface text-success-ink border-success-line',
  medium: 'bg-warning-surface text-warning-ink border-warning-line',
  high: 'bg-danger-surface text-danger-ink border-danger-line',
};

function determineLevel(score: number, requiredScore?: number | null) {
  if (!Number.isFinite(score)) {
    return null;
  }
  if (score < 0) {
    return 'good';
  }
  const target = Number.isFinite(requiredScore) && requiredScore ? Math.max(requiredScore, 0.1) : 5;
  const ratio = score / target;
  if (ratio < 0.5) {
    return 'low';
  }
  if (ratio < 1) {
    return 'medium';
  }
  return 'high';
}

function formatScore(score?: number | null) {
  if (!Number.isFinite(score ?? null)) {
    return '—';
  }
  // Truncate rather than round so displayed score matches rule evaluation
  const truncated = Math.floor(Number(score) * 10) / 10;
  return truncated.toFixed(1);
}

function getTopSymbols(report?: SpamReport | null) {
  if (!report || typeof report !== 'object' || !report.symbols || typeof report.symbols !== 'object') {
    return [] as Array<{ name: string; score: number; description?: string }>;
  }
  type SymbolEntry = { name: string; score: number; description?: string };

  const entries: SymbolEntry[] = Object.entries(report.symbols)
    .map(([name, value]) => {
      const symbolScore = typeof value?.score === 'number' ? value.score : Number(value?.score);
      if (!Number.isFinite(symbolScore)) {
        return null;
      }
      const description = typeof value?.description === 'string' ? value.description : undefined;
      const entry: SymbolEntry = { name, score: symbolScore, description };
      return entry;
    })
    .filter((entry): entry is SymbolEntry => entry !== null);

  return entries
    .sort((a, b) => Math.abs(b.score) - Math.abs(a.score))
    .slice(0, 4);
}

export function SpamScoreBadge({
  score,
  requiredScore,
  action,
  report,
  scannedAt,
  size = 'sm',
  className,
}: SpamScoreBadgeProps) {
  if (score === null || score === undefined) {
    return null;
  }

  const level = determineLevel(score, requiredScore) || 'low';
  const badgeClass = LEVEL_STYLES[level] || LEVEL_STYLES.low;
  const padding = size === 'md' ? 'px-2.5 py-1 text-xs' : 'px-2 py-0.5 text-[11px]';
  const topSymbols = getTopSymbols(report);

  const badge = (
    <Badge
      className={cn(
        'rounded-full border font-medium leading-none shadow-sm backdrop-blur-sm',
        padding,
        badgeClass,
        className,
      )}
    >
      <span className="tabular-nums">{formatScore(score)}</span>
    </Badge>
  );

  return (
    <Tooltip delayDuration={200}>
      <TooltipTrigger asChild>{badge}</TooltipTrigger>
      <TooltipContent className=" space-y-2 text-xs">
        <div className="font-semibold text-base">Spam analysis</div>
        <div className="flex items-center gap-2 border-b border-t pt-2 pb-2">
          <div className="border-r pr-2" >
            <span className="block text-[11px] uppercase tracking-wide">Score</span>
            <span className="font-medium">{formatScore(score)}</span>
          </div>
          <div className="border-r pr-2">
            <span className="block text-[11px] uppercase tracking-wide">Required</span>
            <span className="font-medium">
              {Number.isFinite(requiredScore ?? null) ? Number(requiredScore).toFixed(1) : '—'}
            </span>
          </div>
        
        {action && (
          <div>
            <span className="block text-[11px] uppercase tracking-wide">Action</span>
            <span className="font-medium">{action}</span>
          </div>
        )}
        </div>
        {topSymbols.length > 0 ? (
          <div className="space-y-1">
            <div className="text-[11px] uppercase tracking-wide">Key symbols</div>
            <ul className="space-y-2">
              {topSymbols.map((symbol) => (
                <li key={symbol.name} className="flex flex-col items-start gap-1">
                  
                  <div className="flex items-center gap-1">
                    <div className=" font-medium">{symbol.name}</div>
                    <span className=" font-semibold tabular-nums">
                      {(Math.floor(symbol.score * 10) / 10).toFixed(1)}
                    </span>
                  </div>
                  
                    
                    {symbol.description && (
                      <div className="text-[11px]">{symbol.description}</div>
                    )}
                  
                </li>
              ))}
            </ul>
          </div>
        ) : (
          <p className="">Detailed symbol breakdown not available.</p>
        )}
        {scannedAt && (
          <div className="text-[11px]">
            Updated {new Date(scannedAt).toLocaleString()}
          </div>
        )}
      </TooltipContent>
    </Tooltip>
  );
}
